class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

if (!customElements.get("cart-remove-button")) {
  customElements.define("cart-remove-button", CartRemoveButton);
}

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.cartUpdateUnsubscriber = undefined;

    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    if (document.querySelector(".cart-shipping")) {
      const isFirstLoad = !document.body.dataset.cartShippingInitialized;
      if (isFirstLoad) {
        document.body.dataset.cartShippingInitialized = "true";
      }
      this.initCartShipping({ isInitLaunch: isFirstLoad });
    }

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  initCartShipping({ isInitLaunch = false } = {}) {
    document.querySelectorAll(".cart-shipping").forEach((el) => {
      const progressEl = el.querySelector(".cart-shipping__progress-current");
      if (!progressEl) return;
      let progressPrev =
        getComputedStyle(progressEl)?.getPropertyValue("width");
      document.documentElement.style.setProperty(
        "--shipping-progress-prev",
        progressPrev
      );

      const total = el.dataset.total;
      const minSpend = el.dataset.minSpend;
      const messageTemplate = el.dataset.message;
      const hasConfetti = el.dataset.hasConfetti === "true";

      const minTotal = Math.round(minSpend * (Shopify.currency.rate || 1));
      let progress = (total / minTotal) * 100;
      if (progress > 100) progress = 100;

      const msgDefault = el.querySelector(".cart-shipping__message_default");
      const msgValueEl = msgDefault?.querySelector("[data-shipping-message]");
      const msgSuccess = el.querySelector(".cart-shipping__message_success");

      if (minTotal > total) {
        let amount = minTotal - total;
        // formatMoney - func in global.js
        let message = messageTemplate.replace("[amount]", formatMoney(amount));
        if (msgDefault) msgDefault.classList.add("active");
        if (msgValueEl) msgValueEl.innerHTML = message;
        if (msgSuccess) msgSuccess.classList.remove("active");
        document.body.classList.remove("shipping-goal-reached");
      } else {
        if (msgDefault) msgDefault.classList.remove("active");
        if (msgSuccess) msgSuccess.classList.add("active");

        if (isInitLaunch) {
          document.body.classList.add("shipping-goal-reached");
        }

        const wasSuccess = document.body.classList.contains(
          "shipping-goal-reached"
        );
        const parentElement =
          document.querySelector("cart-drawer[open]") ||
          document.querySelector("#main-cart-shipping");

        if (hasConfetti && !isInitLaunch && parentElement && !wasSuccess) {
          this.fireConfetti();
          document.body.classList.add("shipping-goal-reached");
        }
      }

      progressEl.style.width = progress + "%";
    });
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute("name")
    );
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const sourceQty = html.querySelector("cart-items");
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-icon-bubble-sidebar",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-header",
        section: document.getElementById("main-cart-header")?.dataset?.id,
        selector: ".js-contents-heading",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer")?.dataset?.id,
        selector: ".js-contents-totals",
      },
      {
        id: "main-cart-shipping",
        section:
          document.getElementById("main-cart-shipping")?.dataset?.id || null,
        selector: ".js-contents-shipping",
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    if (document.querySelector(".cart-shipping__progress")) {
      let progressPrev = getComputedStyle(
        document.querySelector(".cart-shipping__progress-current")
      ).getPropertyValue("width");
      document.documentElement.style.setProperty(
        "--shipping-progress-prev",
        progressPrev
      );
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");
        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);

          // [Start theme custom event] - Cart error event
          document.dispatchEvent(
            new CustomEvent("cart:error", {
              detail: {
                errors: parsedState.errors,
                message: parsedState.errors,
              },
            })
          );
          // [End theme custom event] - Cart error event

          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartPageWrapper = document.querySelector(".main-cart__wrapper");
        const parentCartDrawer = this.closest("cart-drawer");

        if (cartDrawerWrapper) {
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );
        }
        if (cartPageWrapper) {
          cartPageWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );
        }
        if (parentCartDrawer) {
          parentCartDrawer.productId = parsedState.items[0]?.product_id;
        }

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              ?.querySelector(section.selector) ||
            document.getElementById(section.id);
          if (!elementToReplace) return;
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;

        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".cart-drawer__empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name")
          );
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });

        // [Start theme custom event] - Cart item change event
        document.dispatchEvent(
          new CustomEvent("line-item:change", {
            detail: {
              lineItem: parsedState.items[line - 1] || null,
              cart: parsedState,
            },
          })
        );
        // [End theme custom event] - Cart item change event

        // [Start theme custom event] - Cart change event
        document.dispatchEvent(
          new CustomEvent("cart:change", {
            detail: {
              cart: parsedState,
            },
          })
        );
        // [End theme custom event] - Cart change event
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        if (document.querySelector(".cart-shipping")) {
          this.initCartShipping({ isInitLaunch: false });
        }
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").innerHTML = message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const cartLine =
      this.querySelector(`#CartItem-${line}`) ||
      this.querySelector(`#CartDrawer-Item-${line}`);
    if (cartLine) {
      cartLine.classList.add("disabled");
      const loadingElement = cartLine.querySelector(".loading-overlay");
      if (loadingElement) loadingElement.classList.remove("hidden");
    }

    const cartFooter =
      document.querySelector(".main-cart__footer") ||
      document.querySelector(".cart-drawer__footer");
    if (cartFooter) {
      const subtotalValue =
        cartFooter.querySelector(".main-cart__subtotal-value") ||
        cartFooter.querySelector(".cart-drawer__subtotal-value");
      if (subtotalValue) subtotalValue.classList.add("disabled");
      const loadingElement = cartFooter.querySelector(".loading-overlay");
      if (loadingElement) loadingElement.classList.remove("hidden");
    }

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const cartLine =
      this.querySelector(`#CartItem-${line}`) ||
      this.querySelector(`#CartDrawer-Item-${line}`);
    if (cartLine) {
      cartLine.classList.remove("disabled");
      const loadingElement = cartLine.querySelector(".loading-overlay");
      if (loadingElement) loadingElement.classList.add("hidden");
    }

    const cartFooter =
      document.querySelector(".main-cart__footer") ||
      document.querySelector(".cart-drawer__footer");
    if (cartFooter) {
      const subtotalValue =
        cartFooter.querySelector(".main-cart__subtotal-value") ||
        cartFooter.querySelector(".cart-drawer__subtotal-value");
      if (subtotalValue) subtotalValue.classList.remove("disabled");
      const loadingElement = cartFooter.querySelector(".loading-overlay");
      if (loadingElement) loadingElement.classList.add("hidden");
    }
  }

  fireConfetti() {
    if (!window.confetti || typeof window.confetti !== "function") return;

    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 60,
      zIndex: 1000,
    };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function () {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      window.confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      window.confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);
  }
}

if (!customElements.get("cart-items")) {
  customElements.define("cart-items", CartItems);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.header = document.querySelector(".header-wrapper");
    if (this.header) this.header.preventHide = false;

    this.setHeaderCartIconAccessibility();
    this.addEventListener("keyup", (event) => {
      event.code === "Escape" && this.close();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".js-trigger-cart-drawer");
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.addEventListener("click", (event) => {
        event.preventDefault();
        if (!this.getAttribute("open")) {
          this.open(cartLink);
        } else {
          this.close();
        }
      });
    });
  }

  open(triggeredBy) {
    if (this.header) this.header.preventHide = true;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.getRecommendationsProducts();

    this.setAttribute("open", true);

    this.addEventListener(
      "transitionend",
      () => {
        const containerTrapFocus = document.getElementById("CartDrawer");
        const focusElement = this.querySelector(".cart-drawer__inner");
        trapFocus(containerTrapFocus, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden-drawer");
  }

  close() {
    this.removeAttribute("open");
    this.productId = "";

    if (this.activeElement) {
      let activeElement = this.activeElement;
      const productCard = this.activeElement.closest("product-card");
      const productCardLink = productCard?.querySelector(
        ".product-card__link-overlay"
      );
      if (productCardLink) activeElement = productCardLink;

      removeTrapFocus(activeElement);
    }

    const cartNoteModal = this.querySelector("cart-note-modal");
    if (cartNoteModal && typeof cartNoteModal.onClose === "function") {
      cartNoteModal.onClose();
    }

    document.body.classList.remove("overflow-hidden-drawer");

    if (this.header) this.header.preventHide = false;
  }

  renderContents(parsedState) {
    if (this.classList.contains("is-empty")) {
      this.classList.remove("is-empty");
    }
    this.productId = parsedState.product_id;
    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id)?.querySelector(section.selector) ||
        document.getElementById(section.id);
      if (!elementToReplace) return;
      elementToReplace.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.section],
        section.selector
      );
    });

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".cart-drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-icon-bubble-sidebar",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getRecommendationsProducts() {
    const recommendationsEl = this.querySelector(
      ".cart-drawer__recommendations"
    );
    if (!recommendationsEl) return;

    let fetchUrl = recommendationsEl.dataset.fetchUrl;
    if (!fetchUrl) return;

    if (this.productId) {
      const baseUrl = recommendationsEl.dataset.baseUrl;
      if (baseUrl) {
        fetchUrl = baseUrl.replace("[product_id]", this.productId);
      }
    }

    const oldList = recommendationsEl.querySelector(
      ".cart-drawer__recommendations-list"
    );
    if (oldList) oldList.remove();

    const loadingEl = recommendationsEl.querySelector(
      ".cart-drawer__recommendations-loading"
    );
    if (loadingEl) loadingEl.style.display = "flex";

    fetch(fetchUrl)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendationsList = html.querySelector(
          ".cart-drawer__recommendations-list"
        );
        if (!recommendationsList) {
          recommendationsEl.style.display = "none";
          return;
        }

        recommendationsEl.appendChild(recommendationsList);
        trapFocus(this, this.querySelector(".cart-drawer__inner"));
      })
      .catch((error) => {
        console.error("Failed to load recommendations products:", error);
      })
      .finally(() => {
        if (loadingEl) loadingEl.style.display = "none";
      });
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          const parentDrawer = this.closest("cart-drawer");
          if (
            parentDrawer &&
            typeof parentDrawer.getRecommendationsProducts === "function"
          ) {
            parentDrawer.getRecommendationsProducts();
          }
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".cart-drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-icon-bubble-sidebar",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

if (!customElements.get("cart-drawer-items")) {
  customElements.define("cart-drawer-items", CartDrawerItems);
}

class CartDrawerSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initSlider();
  }

  initSlider() {
    const sliderSettings = {
      loop: true,
      slidesPerView: 1,
      spaceBetween: 12,
      speed: 800,
      watchSlideProgress: true,
      allowTouchMove: true,
      mousewheel: {
        forceToAxis: true,
      },
      autoplay: false,
      pagination: {
        el: this.querySelector(".slider-pagination"),
        clickable: true,
      },
      navigation: false,
    };

    new Swiper(this, sliderSettings);
  }
}

if (!customElements.get("cart-drawer-products-slider")) {
  customElements.define("cart-drawer-products-slider", CartDrawerSlider);
}

class CartNote extends HTMLElement {
  constructor() {
    super();
    this.spinnerIcon = this.querySelector(".cart-note__loading");
    this.successIcon = this.querySelector(".cart-note__success");
    this.textarea = this.querySelector("textarea");
    this.cartButtons =
      document.querySelector(".main-cart__buttons") ||
      document.querySelector(".cart-drawer__buttons");
    this.submitBtn = this.querySelector(".cart-note__submit");

    this.value = this.textarea.value;

    this.textarea?.addEventListener("input", (event) => {
      this.value = event.target.value;

      if (event.target.value === "") {
        this.onSubmitForm(event);
      }
    });

    this.submitBtn?.addEventListener("click", this.onSubmitForm.bind(this));
  }

  onSubmitForm(event) {
    event.preventDefault();

    if (event.type === "click" && this.value === "") return;

    this.enableLoading();

    const body = JSON.stringify({ note: this.value });

    fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      ...{ body },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        if (data.note !== "") {
          console.info(`Order note "${data.note}" saved successfully.`);
        } else {
          console.info("Order note removed successfully.");
        }

        if (this.successIcon) this.successIcon.style.display = "flex";

        setTimeout(() => {
          if (this.successIcon) this.successIcon.style.display = "none";
        }, 2000);
      })
      .catch((error) => {
        console.error("There was a problem with the fetch operation:", error);
      })
      .finally(() => {
        this.disableLoading();
        this.updateHtmlStatus();
      });
  }

  enableLoading() {
    if (this.spinnerIcon) this.spinnerIcon.style.display = "flex";
    if (this.successIcon) this.successIcon.style.display = "none";
    if (this.submitBtn) {
      this.submitBtn.classList.add("loading");
      const loader = this.submitBtn.querySelector(".button-loader");
      if (loader) loader.classList.remove("hidden");
    }
    if (this.cartButtons) {
      this.cartButtons.style.pointerEvents = "none";
      this.cartButtons.style.opacity = "0.7";
    }
  }

  disableLoading() {
    if (this.spinnerIcon) this.spinnerIcon.style.display = "none";
    if (this.submitBtn) {
      this.submitBtn.classList.remove("loading");
      const loader = this.submitBtn.querySelector(".button-loader");
      if (loader) loader.classList.add("hidden");
    }
    if (this.cartButtons) {
      this.cartButtons.removeAttribute("style");
    }
  }

  updateHtmlStatus() {
    const noteStatusElements = document.querySelectorAll(
      ".js-cart-note-status"
    );
    noteStatusElements.forEach((el) => {
      if (this.value.trim() === "") {
        el.textContent = document.querySelector(
          "[data-cart-note-add-label]"
        ).textContent;
      } else {
        el.textContent = document.querySelector(
          "[data-cart-note-edit-label]"
        ).textContent;
      }
    });
  }
}

if (!customElements.get("cart-note")) {
  customElements.define("cart-note", CartNote);
}

class CartNoteModal extends HTMLElement {
  constructor() {
    super();

    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);
  }

  connectedCallback() {
    this.modalEl = this.querySelector(".cart-drawer__note-modal");
    this.openBtn = this.querySelector(".cart-drawer__note-open");
    this.closeBtn = this.querySelector(".cart-drawer__note-close");
    this.textarea = this.querySelector("textarea");

    this.openBtn?.addEventListener("click", this.onOpen);
    this.closeBtn?.addEventListener("click", this.onClose);
    this.modalEl?.addEventListener("transitionend", this.onTransitionEnd);
  }

  disconnectedCallback() {
    this.openBtn?.removeEventListener("click", this.onOpen);
    this.closeBtn?.removeEventListener("click", this.onClose);
    this.modalEl?.removeEventListener("transitionend", this.onTransitionEnd);
  }

  onOpen() {
    this.modalEl?.classList.add("cart-note-open");
  }

  onClose() {
    this.modalEl?.classList.remove("cart-note-open");
  }

  onTransitionEnd(event) {
    if (event.target !== this.modalEl) return;
    if (this.modalEl.classList.contains("cart-note-open")) {
      trapFocus(this.modalEl, this.textarea);
    } else {
      removeTrapFocus(this.openBtn);
    }
  }
}

if (!customElements.get("cart-note-modal")) {
  customElements.define("cart-note-modal", CartNoteModal);
}

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.header = document.querySelector(".header-wrapper");
    if (this.header) this.header.preventHide = false;

    this.itemEl = this.querySelector("#cart-notification-item");

    this._onOutsideClick = this.onOutsideClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener("keyup", (event) => {
      event.code === "Escape" && this.close();
    });
    document.addEventListener("click", this._onOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this._onOutsideClick);
  }

  open(triggeredBy) {
    if (this.header) this.header.preventHide = true;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.setAttribute("open", true);

    this.addEventListener(
      "transitionend",
      () => {
        if (this.getAttribute("open")) {
          const containerTrapFocus =
            document.getElementById("CartNotification");
          const focusElement = this.querySelector(".cart-notification__inner");
          trapFocus(containerTrapFocus, focusElement);
        }
      },
      { once: true }
    );

    if (this.closeSetTimeout) clearTimeout(this.closeSetTimeout);
    this.closeSetTimeout = setTimeout(() => {
      if (this.getAttribute("open")) {
        this.close();
      }
    }, 8000);
  }

  close() {
    this.removeAttribute("open");
    this.productId = "";

    if (this.closeSetTimeout) {
      clearTimeout(this.closeSetTimeout);
      this.closeSetTimeout = null;
    }

    if (this.activeElement) {
      let activeElement = this.activeElement;
      const productCard = this.activeElement.closest("product-card");
      const productCardLink = productCard?.querySelector(
        ".product-card__link-overlay"
      );
      if (productCardLink) activeElement = productCardLink;

      removeTrapFocus(activeElement);
    }

    if (this.header) this.header.preventHide = false;
    this.cartItemKey = "";

    this.addEventListener(
      "transitionend",
      () => {
        if (!this.getAttribute("open")) {
          if (this.itemEl) this.itemEl.innerHTML = "";
        }
      },
      { once: true }
    );
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    this.getSectionsToRender().forEach((section) => {
      if (section.section === "cart-notification-items") {
        const addedItemSource = this.getParsedElement(
          parsedState.sections[section.section],
          section.selector
        );

        if (addedItemSource && this.itemEl) {
          this.itemEl.innerHTML = "";
          this.itemEl.append(addedItemSource);
        }
        return;
      }

      const elementToReplace =
        document.getElementById(section.id)?.querySelector(section.selector) ||
        document.getElementById(section.id);
      if (!elementToReplace) return;
      elementToReplace.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.section],
        section.selector
      );
    });

    setTimeout(() => {
      this.open();
    });
  }

  getParsedElement(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".cart-drawer__inner",
      },
      {
        id: "CartNotification",
        section: "cart-notification-items",
        selector: `[id="CartNotification-Item-${this.cartItemKey}"]`,
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-icon-bubble-sidebar",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  onOutsideClick(event) {
    if (this.getAttribute("open") !== "true" || this.contains(event.target)) {
      return;
    }
    this.close();
  }
}

if (!customElements.get("cart-notification")) {
  customElements.define("cart-notification", CartNotification);
}
